import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRippleModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BpCoreModule } from '@bp-core/src/lib/bp-core.module';
import { INewCustomCategoryFund } from '@bp-core/src/lib/models/portal/goal/new-custom-category-fund.model';
import { INewCustomFund } from '@bp-core/src/lib/models/portal/goal/new-custom-fund.model';
import { BaseGetRequest, FundingModel, TagModel } from '@bp-core/src/lib/proto/common-message.pb';
import {
  GetSummaryCategoryDetailRequest,
  ListCategorySummaryByFinancialEntityRequest,
  ListCategorySummaryDetail,
  SummaryFunding,
} from '@bp-core/src/lib/proto/funding.pb';
import { FundingGrpcServiceClient } from '@bp-core/src/lib/proto/funding.pbsc';
import { environment } from '@environment/environment';
import { fuseAnimations } from '@fuse/animations';
import { StringValue } from '@ngx-grpc/well-known-types';
import { GoalFundingService } from 'bp/shared/services/goal-funding.service';
import _ from 'lodash';
import { combineLatest, startWith, Subject, take, takeUntil } from 'rxjs';
import { DiscoverFundAvatarComponent } from '../discover-fund-avatar/discover-fund-avatar.component';
import { FundsDetailSearchFundsComponent } from '../funds-detail-search-funds/funds-detail-search-funds.component';

@Component({
  selector: 'search-funds-drawer',
  templateUrl: './search-funds-drawer.component.html',
  styleUrls: ['./search-funds-drawer.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    BpCoreModule,
    MatRippleModule,
    MatDividerModule,
    FundsDetailSearchFundsComponent,
    DiscoverFundAvatarComponent,
    MatButtonToggleModule,
  ],
})
export class SearchFundsDrawerComponent implements OnInit, OnChanges, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  searchForm: UntypedFormGroup;
  subCategoryWithFundsAddedTmp: INewCustomCategoryFund[] = [];
  subCategoryWithFundsFiltered: INewCustomCategoryFund[] = [];
  toggleControl = new FormControl<number[]>([]);
  searchControl = new FormControl<string>('');

  subCategoryWithFundsAddedTmp2: SummaryFunding[] = [];

  categorySummaryDetail: ListCategorySummaryDetail[];
  customCategorySelected: INewCustomCategoryFund;
  categorySelected: ListCategorySummaryDetail;
  summaryFundingSelected: SummaryFunding | undefined;
  fundSelected: FundingModel | undefined;

  currentPage = 'category-list';
  lastPage = 'category-list ';
  customFundSelected: INewCustomFund;
  urlFileBase = environment.urlApiV2 + 'api/Files/';
  token = localStorage.getItem('access_token');
  isLoadingFunds = signal(false);
  isLoadingSpecificFund = signal(false);
  filteredFunds: SummaryFunding[] = [];
  fundingTags: TagModel[] | undefined;

  @Input() drawerOpened = false;
  @Input() financialEntityId: number | undefined;
  @Input() isFundingDetail = false;
  @Input() funding: FundingModel | undefined;

  @Input() subCategoryWithFundsAdded: INewCustomCategoryFund[] = [];

  @Input() subCategoryWithFunds: INewCustomCategoryFund[];

  @Output() close = new EventEmitter<void>();

  @Output() onAddCategoryFund = new EventEmitter<INewCustomCategoryFund>();
  @Output() onRemoveCategoryFund = new EventEmitter<INewCustomCategoryFund>();
  @ViewChild('backButton') private backButton: ElementRef;

  get startPage() {
    return this.currentPage === 'category-list';
  }

  get search() {
    return this.searchForm?.get('search');
  }

  constructor(
    private fb: UntypedFormBuilder,
    public goalFundingService: GoalFundingService,
    public fundingGrpcServiceClient: FundingGrpcServiceClient,
  ) {}
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    // Cleanup any subscriptions or resources if necessary
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.subCategoryWithFundsAdded?.currentValue) {
      this.subCategoryWithFundsAddedTmp = [...this.subCategoryWithFundsAdded];
      const flatCustomFunds = this.subCategoryWithFundsAddedTmp.flatMap(
        customCategoryFund => customCategoryFund.customFunds,
      );
      this.subCategoryWithFundsAddedTmp2 = flatCustomFunds.map(
        customFund =>
          new SummaryFunding({
            fundingId: customFund.fund?.fund?.id,
            description: new StringValue({
              value: customFund.fund.fund?.description || '',
            }),
            mnemonic: new StringValue({
              value: customFund.fund.fund?.mnemonic || '',
            }),
            title: customFund.fund.fund?.title || '',
          }),
      );
    }
    if (changes.subCategoryWithFunds?.currentValue) {
      this.subCategoryWithFundsFiltered = [...this.subCategoryWithFunds];
    }
    if (changes.drawerOpened?.currentValue) {
      this.currentPage = 'category-list';

      this.subCategoryWithFundsFiltered = [...this.subCategoryWithFunds];
      this.search?.setValue('');
    }
    if (this.isFundingDetail === true) {
      this.currentPage = 'fund-detail';
    } else {
      this.currentPage = 'category-list';
    }
  }
  ngOnInit(): void {
    this.searchForm = this.fb.group({
      search: '',
    });
    this.fundingGrpcServiceClient
      .listCategorySummaryByFinancialEntity(
        new ListCategorySummaryByFinancialEntityRequest({
          financialEntityId: this.financialEntityId,
        }),
      )
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.categorySummaryDetail = response.categories ?? [];
          this.subCategoryWithFundsFiltered = this.categorySummaryDetail.map(subCategory => ({
            category: subCategory,
            customFunds: [],
          }));
          console.log('Category Summary Detail:', this.categorySummaryDetail);
        },
      });

    this.searchForm.valueChanges.subscribe(() => {
      this.filterSubCategoryWithFunds();
    }); // Observables para los cambios de los filtros
    const toggleControlChanges$ = this.toggleControl.valueChanges.pipe(
      startWith(this.toggleControl.value),
    );
    const searchControlChanges$ = this.searchControl.valueChanges.pipe(
      startWith(this.searchControl.value),
    );
    combineLatest([toggleControlChanges$, searchControlChanges$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([selectedTags, searchTerm]) => {
        let filteredByTags = this.customCategorySelected?.customFunds?.flatMap(fund => fund.fund);

        // Primero aplicamos el filtro de tags
        if (selectedTags?.length! > 0) {
          filteredByTags = this.customCategorySelected?.customFunds
            ?.flatMap(fund => fund.fund)
            .filter(fund => {
              return selectedTags?.every(selectedTagId =>
                fund.fundingTags?.some(fundingTag => fundingTag.tag?.id === selectedTagId),
              );
            });
        }

        // Luego aplicamos el filtro de búsqueda
        this.filteredFunds = filteredByTags?.filter(fund => {
          const searchLower = searchTerm?.toLowerCase() || '';
          // Aquí puedes ajustar qué campo de `fund` se compara con el término de búsqueda.
          return (
            fund.title?.toLowerCase().includes(searchLower) ||
            fund.mnemonic?.value?.toLowerCase().includes(searchLower)
          );
        });
      });
  }
  filterSubCategoryWithFunds() {
    const filterValue = this.search?.value.toLowerCase();

    this.subCategoryWithFundsFiltered = this.subCategoryWithFunds
      .map(subCategoryWithFunds => {
        // Filtrar los customFunds que coinciden
        const matchingCustomFunds = subCategoryWithFunds.customFunds.filter(
          customFund =>
            customFund.fund?.title.toLowerCase().includes(filterValue) ||
            customFund.fund.fund?.mnemonic.toLowerCase().includes(filterValue),
        );

        return {
          ...subCategoryWithFunds,
          // Verificar si la categoría o alguno de los customFunds coinciden
          customFunds: subCategoryWithFunds.category.categoryDisplayName
            .toLowerCase()
            .includes(filterValue)
            ? subCategoryWithFunds.customFunds // Si la categoría coincide, mantener todos los customFunds
            : matchingCustomFunds, // De lo contrario, usar solo los customFunds coincidentes
        };
      })
      .filter(
        subCategoryWithFunds =>
          subCategoryWithFunds.category.categoryDisplayName.toLowerCase().includes(filterValue) ||
          subCategoryWithFunds.customFunds.length > 0,
      );
  }

  selectCustomFund(summaryFunding: SummaryFunding) {
    this.summaryFundingSelected = summaryFunding;
    this.customFundSelected = {
      fund: summaryFunding,
      userPercentage: 0,
      defaultPercentage: 0,
      color: '',
      groupCategoryDisplayOrder: 0,
    };

    this.isLoadingSpecificFund.set(true);
    this.fundingGrpcServiceClient
      .get(
        new BaseGetRequest({
          where: {
            predicate: new StringValue({
              value: `id = ${summaryFunding.fundingId}`,
            }),
          },
          include: 'FundingTags,FundingComposition',
        }),
      )
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.fundSelected = response;
          this.isLoadingSpecificFund.set(false);
          this.summaryFundingSelected!.fund = response;
          // this.currentPage = 'fund-detail';
        },
        error: error => {
          console.error('Error fetching specific fund:', error);
          this.isLoadingSpecificFund.set(false);
        },
      });
  }

  selectCategory(categorySummaryDetail: INewCustomCategoryFund) {
    this.isLoadingFunds.set(true);
    this.fundingGrpcServiceClient
      .getSummaryCategoryDetail(
        new GetSummaryCategoryDetailRequest({
          financialEntityId: this.financialEntityId,
          categoryId: categorySummaryDetail.category.categoryId,
        }),
      )
      .pipe(take(1))
      .subscribe({
        next: response => {
          //filtra los funds de response.funds para no tener los funds de  this.subCategoryWithFundsAdded
          response.funds = response.funds!.filter(fund => !fund.isDefaultCustomFund);
          response.funds?.sort((a, b) => {
            // Proporciona un valor predeterminado para los títulos en caso de que sean undefined
            const titleA = a.title || '';
            const titleB = b.title || '';

            return titleA.localeCompare(titleB);
          });

          categorySummaryDetail.category = response;
          this.fundingTags = this.getCurrentTags(response.funds!);
          this.categorySelected = response;
          this.customCategorySelected = {
            category: response,
            customFunds: response?.funds!.map(fund => ({
              fund: fund,
              userPercentage: 0,
              defaultPercentage: 0,
              color: '',
              groupCategoryDisplayOrder: 0,
            })),
          };
          this.filteredFunds = this.customCategorySelected.customFunds.map(
            customFund => customFund.fund,
          );

          this.isLoadingFunds.set(false);
        },
        error: error => {
          console.error('Error fetching category details:', error);
          this.isLoadingFunds.set(false);
        },
      });

    // this.customCategorySelected = customCategory;
    // this.customCategorySelected.customFunds.sort((a: ICustomFund, b: ICustomFund) => {
    //   // Proporciona un valor predeterminado para los títulos en caso de que sean undefined
    //   const titleA = a.fund.fund?.title || '';
    //   const titleB = b.fund.fund?.title || '';

    //   return titleA.localeCompare(titleB);
    // });
  }
  back() {
    if (this.isFundingDetail) {
      this.close.emit();
      return;
    }

    switch (this.currentPage) {
      case 'fund-detail':
        if (this.lastPage === 'category-list') {
          this.currentPage = 'category-list';
          break;
        }
        this.currentPage = 'funds-list';

        break;
      case 'funds-list':
        this.currentPage = 'category-list';

        break;
      case 'category-list':
        this.close.emit();
        break;
    }
    this.lastPage = this.currentPage;
  }
  next(route?: string) {
    this.lastPage = this.currentPage;
    this.backButton.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (route) {
      this.currentPage = route;
      return;
    }

    switch (this.currentPage) {
      case 'category-list':
        this.currentPage = 'funds-list';

        break;
      case 'funds-list':
        this.currentPage = 'fund-detail';

        break;
      case 'fund-detail':
        this.addCustomCategoryFund(this.summaryFundingSelected!);
        this.currentPage = 'funds-list';

        break;
    }
  }

  addCustomCategoryFund(summaryFunding: SummaryFunding | undefined) {
    if (!this.isCustomFundAdded(summaryFunding?.fundingId)) {
      this.subCategoryWithFundsAddedTmp2.push(summaryFunding!);
    }

    this.onAddCategoryFund.emit({
      category: this.categorySelected,
      customFunds: [
        {
          fund: summaryFunding!,
          userPercentage: 0,
          defaultPercentage: 0,
          color: '',
          groupCategoryDisplayOrder: 0,
        },
      ],
    });
  }
  removeCustomCategoryFund(customFund: INewCustomFund) {
    const customCategoryFund = this.subCategoryWithFundsAddedTmp.find(
      customCategoryFund =>
        customCategoryFund.category.categoryId === customFund.category?.categoryId,
    );
    if (customCategoryFund) {
      customCategoryFund.customFunds = customCategoryFund.customFunds.filter(
        cf => cf.fund.fund?.id !== customFund.fund.fund?.id,
      );
      if (customCategoryFund.customFunds.length === 0) {
        this.subCategoryWithFundsAddedTmp = this.subCategoryWithFundsAddedTmp.filter(
          cf => cf.category.categoryId !== customCategoryFund.category.categoryId,
        );
      }
    }
    this.subCategoryWithFundsAddedTmp2 = this.subCategoryWithFundsAddedTmp2.filter(
      customFundAdded => customFundAdded.fundingId !== customFund.fund.fund?.id,
    );
    // Emit the event to remove the custom fund
    this.onRemoveCategoryFund.emit({
      category: customFund.category!,
      customFunds: [customFund],
    });

    // this.onRemoveCategoryFund.emit({
    //   category: customFund.category!,
    //   customFunds: [customFund],
    // });
  }
  isCustomFundAdded(fundingId?: number) {
    if (this.isFundingDetail) {
      return this.subCategoryWithFundsAddedTmp2.some(
        customFundAdded => customFundAdded.fundingId === fundingId,
      );
    }
    return this.subCategoryWithFundsAddedTmp2.some(
      customFundAdded => customFundAdded.fundingId === fundingId,
    );
  }
  goShowBrochure(fund: FundingModel) {
    if (fund?.informativeBrochureUrl !== '') {
      window.open(fund?.informativeBrochureUrl!, '_blank', 'noopener');
      return;
    }
    window.open(
      this.urlFileBase + fund?.informativeBrochureId?.value + '/?token=' + this.token,
      '_blank',
      'noopener',
    );
  }
  getCurrentTags(funds: SummaryFunding[]) {
    const tags = funds
      ?.map(fund => fund.fundingTags ?? [])
      .flat()
      .filter(fundingTag => fundingTag.tag?.showInFilter)
      .sort((a, b) => a.order - b.order)
      .flatMap(fundingTag => fundingTag.tag ?? []);

    // Eliminar duplicados usando lodash uniqBy
    const uniqueTags = _.uniqBy(tags, 'id');

    return uniqueTags;
  }
}
